import { Box, Typography } from "@mui/material"
import type { FC } from "react"

import { formatCostBreakdownCurrency } from "../utils/formatCostBreakdownCurrency"
import { getAtypicalSurchargeTypeLabel } from "../utils/getAtypicalSurchargeTypeLabel"

import { LabelValue } from "./LabelValue"
import { SectionHeading } from "./SectionHeading"
import { TariffsLabelValues } from "./TariffsLabelValues"

import type { GrLineCostBreakdown } from "@/graphql/codegen/graphql"

interface PurchaseOrderLineCostBreakdownExpandedContentProps {
  lineCostBreakdown: GrLineCostBreakdown
}

export const PurchaseOrderLineCostBreakdownExpandedContent: FC<PurchaseOrderLineCostBreakdownExpandedContentProps> = ({
  lineCostBreakdown,
}) => {
  return (
    <Box className='m-6 rounded-md bg-white'>
      <SectionHeading>Product Cost Breakdown</SectionHeading>

      <Box className='grid grid-cols-3 gap-x-8 gap-y-3'>
        <Box>
          <LabelValue label='Price per US UoM' value={formatCostBreakdownCurrency(lineCostBreakdown.pricePerUom)} />
          <LabelValue
            label='Price per Metric UoM'
            value={formatCostBreakdownCurrency(lineCostBreakdown.pricePerMetricUom)}
          />
          <LabelValue
            label='Bare Cost per US UoM'
            value={formatCostBreakdownCurrency(lineCostBreakdown.bareCostPerUom)}
          />
          <LabelValue
            label='Bare Cost per Metric UoM'
            value={formatCostBreakdownCurrency(lineCostBreakdown.bareCostPerMetricUom)}
          />
          <LabelValue label='Total Bare Cost' value={formatCostBreakdownCurrency(lineCostBreakdown.totalBareCost)} />
          <LabelValue label='Insurance' value={formatCostBreakdownCurrency(lineCostBreakdown.insurance)} />
          <TariffsLabelValues tariffs={lineCostBreakdown.tariffs} totalBareCost={lineCostBreakdown.totalBareCost!} />
          <LabelValue
            label='Allocated GR Cost per UoM'
            value={formatCostBreakdownCurrency(lineCostBreakdown.allocatedGrCostAmount)}
          />
        </Box>

        <Box>
          <LabelValue label='Duties' value={formatCostBreakdownCurrency(lineCostBreakdown.duties)} />
          <LabelValue label='Merchandise Processing Fee' value={formatCostBreakdownCurrency(lineCostBreakdown.mpf)} />
          <LabelValue label='Harbour Maintenance Fee' value={formatCostBreakdownCurrency(lineCostBreakdown.hmf)} />
          {!!lineCostBreakdown.atypicalSurcharges?.length &&
            lineCostBreakdown.atypicalSurcharges.map((surcharge) => (
              <LabelValue
                key={surcharge.grLineAtypicalSurchargeId}
                label={getAtypicalSurchargeTypeLabel(surcharge.type)}
                value={`${formatCostBreakdownCurrency(surcharge.amount)}${surcharge.description ? ` - ${surcharge.description}` : ""}`}
              />
            ))}
          <LabelValue
            label='Freight and Misc Cost per UoM'
            value={formatCostBreakdownCurrency(lineCostBreakdown.freightAndMiscCostPerUoM)}
          />
        </Box>

        <Box>
          <Typography variant='body2' className='font-normal text-gray-700'>
            Total Laid-Down Cost per UoM:
          </Typography>
          <Typography className='text-lg font-bold text-gray-700'>
            {formatCostBreakdownCurrency(lineCostBreakdown.totalLaidDownCostPerUom)}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
